import { featureOn, plateSearchKey } from "@szerviz/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { NavIcon } from "../components/Icon";
import { Plate } from "../components/Plate";
import { TrialChecklist } from "../components/TrialGuide";
import { PhotoStart, setPendingPhoto } from "../components/VehiclePhoto";
import { useApp } from "../context";
import type { Vehicle, WorkOrder } from "../data";
import { StatusChip } from "./WorkOrders";

/**
 * Workshop home screen, made for the phone: new car intake with one tap,
 * a plate search and the cars in the workshop right now.
 */
export function Home() {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const [active, setActive] = useState<WorkOrder[] | null>(null);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<Vehicle[]>([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    store.searchWorkOrders(workshop.id, "", "active", 100).then(setActive, (e) => {
      setError(e);
      setActive([]);
    });
  }, [store, workshop.id]);

  useEffect(() => {
    if (plateSearchKey(query).length < 2 && query.trim().length < 3) {
      setFound([]);
      return;
    }
    const h = setTimeout(() => void store.searchVehicles(workshop.id, query, 8).then(setFound, setError), 200);
    return () => clearTimeout(h);
  }, [query, store, workshop.id]);

  const ready = (active ?? []).filter((o) => o.status === "ready");
  const remindersOn = featureOn(workshop, "reminders");
  const open = (active ?? []).filter((o) => o.status === "open");

  return (
    <section className="home">
      <h1 className="home-title">
        {t("home.title")} <Help id="home.page" />
      </h1>
      <div className="home-actions">
        <button type="button" className="primary big-action" onClick={() => go("workOrders", "new")}>
          <svg className="gi" viewBox="0 0 48 48" width="32" height="32" aria-hidden="true" focusable="false">
            <rect x="20" y="6" width="8" height="36" style={{ fill: "var(--ico-body, #0E2433)" }} />
            <rect x="6" y="20" width="36" height="8" style={{ fill: "var(--ico-body, #0E2433)" }} />
          </svg>
          {t("home.newIntake")}
        </button>
        <PhotoStart
          onFound={(v) => go("workOrders", `new:${v.id}`)}
          onNew={(r) => {
            setPendingPhoto(r);
            go("workOrders", "new");
          }}
        />
      </div>

      <InstallButton />

      <TrialChecklist />

      {remindersOn && (
        <button type="button" className="home-reminders nav-reminders" onClick={() => go("reminders")}>
          <NavIcon name="reminders" />
          {t("nav.reminders")}
        </button>
      )}

      <label className="field home-search">
        <span className="field-label">{t("home.searchPlate")}</span>
        <input
          type="search"
          className="plate-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t("home.searchPlate")}
          autoCapitalize="characters"
          autoComplete="off"
          enterKeyHint="search"
        />
      </label>
      {found.length > 0 && (
        <ul className="pick-list">
          {found.map((v) => (
            <li key={v.id}>
              <button type="button" onClick={() => go("vehicles", v.id)}>
                <Plate plate={v.plateNumber} country={v.plateCountry} /> {[v.make, v.model].filter(Boolean).join(" ")}
                {v.customerName && <span className="muted"> · {v.customerName}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={error} />

      {active === null ? (
        <p>{t("common.loading")}</p>
      ) : active.length === 0 ? (
        <p className="muted">{t("home.noCars")}</p>
      ) : (
        <>
          {ready.length > 0 && <CarList title={t("home.readyCars", { count: String(ready.length) })} rows={ready} />}
          {open.length > 0 && <CarList title={t("home.openCars", { count: String(open.length) })} rows={open} />}
        </>
      )}
    </section>
  );
}

function CarList({ title, rows }: { title: string; rows: WorkOrder[] }) {
  const { go } = useApp();
  return (
    <>
      <h2>{title}</h2>
      <ul className="car-cards">
        {rows.map((o) => (
          <li key={o.id}>
            <button type="button" className="car-card" onClick={() => go("workOrders", o.id)}>
              <span className="car-card-top">
                <Plate plate={o.plateNumber ?? ""} country={o.plateCountry} />
                <StatusChip status={o.status} />
              </span>
              <span className="car-card-name">{o.vehicleLabel}</span>
              <span className="muted">
                {o.customerName}
                {o.customerRequest && <> · {o.customerRequest}</>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

/** "Put the program on the home screen" when the phone (Android, Chrome) offers it. */
function InstallButton() {
  const { t } = useTranslation();
  const get = () => (globalThis as { installPrompt?: InstallPrompt }).installPrompt ?? null;
  const [prompt, setPrompt] = useState<InstallPrompt | null>(get);
  useEffect(() => {
    const on = () => setPrompt(get());
    globalThis.addEventListener?.("szerviz-installable", on);
    return () => globalThis.removeEventListener?.("szerviz-installable", on);
  }, []);
  if (!prompt) return null;
  return (
    <button
      type="button"
      className="install"
      onClick={async () => {
        await prompt.prompt();
        await prompt.userChoice;
        (globalThis as { installPrompt?: InstallPrompt }).installPrompt = undefined;
        setPrompt(null);
      }}
    >
      {t("home.install")}
    </button>
  );
}
